import { AdductionOrder } from '@features/siteStatusV2/types';
import { OrderStepStatus } from '@features/siteStatusV2/types/order/enums';
import { ThemeVariants } from '@theme';
import React, { useCallback } from 'react';
import { Accordion as AccordionBootstrap } from 'react-bootstrap';
import { Building, Clipboard2DataFill, ClipboardFill, CreditCardFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';
import AccordionSection from './AccordionSection';
import ClientQuoteSection from './ClientQuoteSection';
import ConstructionSection from './ConstructionSection';
import OrderClosureSection from './OrderClosureSection';
import SiteVisitSection from './SiteSection';

interface StepsAccordionProps {
  order: AdductionOrder | null;
}

const StepsAccordion: React.FC<StepsAccordionProps> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);

  const getStepsStatus = useCallback(
    (state: OrderStepStatus) => {
      const statusMap: Record<OrderStepStatus, { color: ThemeVariants; label: OrderStepStatus }> = {
        [OrderStepStatus.FINISHED]: {
          color: 'blue',
          label: t('orderDetails.badges.status.finished')
        },
        [OrderStepStatus.IN_PROGRESS]: {
          color: 'green',
          label: t('orderDetails.badges.status.inProgress')
        },
        [OrderStepStatus.COMING_SOON]: {
          color: 'grey',
          label: t('orderDetails.badges.status.comingSoon')
        }
      };

      return statusMap[state] ?? statusMap[OrderStepStatus.COMING_SOON];
    },
    [t]
  );

  const steps = [
    {
      key: '0',
      header: t('orderDetails.adduction.siteVisit'),
      icon: <Clipboard2DataFill className="icon" />,
      statusKey: order?.fieldVisit?.stepStatus,
      Component: SiteVisitSection
    },
    {
      key: '1',
      header: t('orderDetails.adduction.clientQuote'),
      icon: <ClipboardFill className="icon" />,
      statusKey: order?.quote?.stepStatus,
      Component: ClientQuoteSection
    },
    {
      key: '2',
      header: t('orderDetails.adduction.construction'),
      icon: <Building className="icon" />,
      statusKey: order?.works?.stepStatus,
      Component: ConstructionSection
    },
    {
      key: '3',
      header: t('orderDetails.adduction.orderClosure'),
      icon: <CreditCardFill className="icon" />,
      statusKey: order?.orderCompleted?.stepStatus,
      Component: OrderClosureSection
    }
  ];

  return (
    <Accordion defaultActiveKey={['0']} alwaysOpen>
      {steps.map(({ key, header, icon, statusKey, Component }) => {
        const { label, color } = getStepsStatus(statusKey ?? OrderStepStatus.COMING_SOON);

        return (
          <AccordionSection
            key={key}
            eventKey={key}
            header={header}
            icon={icon}
            statusKey={statusKey}
            status={label}
            colorStatus={color}
          >
            <Component order={order} />
          </AccordionSection>
        );
      })}
    </Accordion>
  );
};

const Accordion = styled(AccordionBootstrap)`
  padding-left: 1.75rem;
  margin-top: 0.75rem;
`;

export default StepsAccordion;
